import { useAuth0 } from '@auth0/auth0-react';
import { ReactNode, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { colors } from '@/theme/tokens';
import { redirectService } from './redirect-service';
import { tokenService } from './token-service';

interface RequireAuthProps {
  children: ReactNode;
}

const MAX_REDIRECTS = 3;
const REDIRECT_WINDOW_MS = 30_000; // 30 seconds
const REDIRECT_KEY = 'tilly_auth_redirects';

function isRedirectBudgetExhausted(): boolean {
  const now = Date.now();
  const raw = window.sessionStorage.getItem(REDIRECT_KEY);
  const entries: number[] = raw ? JSON.parse(raw) : [];
  const recent = entries.filter((t) => now - t < REDIRECT_WINDOW_MS);
  return recent.length >= MAX_REDIRECTS;
}

function recordRedirect(): void {
  const now = Date.now();
  const raw = window.sessionStorage.getItem(REDIRECT_KEY);
  const entries: number[] = raw ? JSON.parse(raw) : [];
  const recent = entries.filter((t) => now - t < REDIRECT_WINDOW_MS);
  recent.push(now);
  window.sessionStorage.setItem(REDIRECT_KEY, JSON.stringify(recent));
}

export function RequireAuth({ children }: RequireAuthProps) {
  const { isAuthenticated, isLoading, loginWithRedirect, error } = useAuth0();
  const location = useLocation();
  const redirectingRef = useRef(false);

  // Bridge mode: a mobile session bridge set an override token — treat as authenticated.
  const hasBridgeSession = tokenService.hasOverrideToken();

  // Computed during render — no state needed.
  const needsRedirect = !isLoading && !isAuthenticated && !error && !hasBridgeSession;
  const loopDetected = needsRedirect && isRedirectBudgetExhausted();

  const handleRetry = useCallback(() => {
    window.sessionStorage.removeItem(REDIRECT_KEY);
    redirectingRef.current = false;
    redirectService.save(location.pathname);
    void loginWithRedirect();
  }, [location.pathname, loginWithRedirect]);

  // Auto-redirect unauthenticated users to Auth0 (with loop protection).
  // The ref prevents duplicate calls (React strict mode fires effects twice)
  // which would overwrite the Auth0 PKCE transaction and break the callback.
  useEffect(() => {
    if (needsRedirect && !loopDetected && !redirectingRef.current) {
      redirectingRef.current = true;
      console.log('[RequireAuth] Not authenticated, redirecting to Auth0…');
      recordRedirect();
      redirectService.save(location.pathname);
      void loginWithRedirect();
    }
  }, [needsRedirect, loopDetected, location.pathname, loginWithRedirect]);

  if (loopDetected) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: colors.bg,
          gap: 2,
          p: 3,
        }}
      >
        <Typography variant="h6" color="error">
          Sign-in loop detected
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          We were redirected to sign in multiple times without success.
          <br />
          Check the browser console for details.
        </Typography>
        <Button
          variant="contained"
          onClick={handleRetry}
          sx={{
            mt: 2,
            bgcolor: colors.brass,
            '&:hover': { bgcolor: colors.brassHover },
          }}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  if ((isLoading && !hasBridgeSession) || (!isAuthenticated && !hasBridgeSession && !error)) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: colors.bg,
          gap: 2,
        }}
      >
        <CircularProgress sx={{ color: colors.accent }} />
        <Typography variant="body2" color="text.secondary">
          {isLoading ? 'Loading...' : 'Redirecting to sign in…'}
        </Typography>
      </Box>
    );
  }

  if (error && !hasBridgeSession) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: colors.bg,
          gap: 2,
          p: 3,
        }}
      >
        <Typography variant="h6" color="error">
          Authentication Error
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {error.message}
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            redirectService.save(location.pathname);
            void loginWithRedirect();
          }}
          sx={{
            mt: 2,
            bgcolor: colors.brass,
            '&:hover': { bgcolor: colors.brassHover },
          }}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  return <>{children}</>;
}
